'use client';

import { Box, Flex, Icon, Input, Text } from '@chakra-ui/react';
import { memo, useCallback, useMemo, useState } from 'react';
import { LuSearch } from 'react-icons/lu';

import {
  ACTION_CATALOG,
  ALL_CATALOG_ITEMS,
  FINANCE_CATALOG,
  ROUTING_CATALOG,
  TRIGGER_CATALOG,
} from './constants';
import { type CatalogItem, PickerMode } from './types';

interface Props {
  mode: PickerMode;
  onSelect: (item: CatalogItem) => void;
  onClose: () => void;
}

const CategorySection = ({
  title,
  items,
  onSelect,
}: {
  title: string;
  items: CatalogItem[];
  onSelect: (item: CatalogItem) => void;
}) => {
  if (items.length === 0) {return null;}
  return (
    <Flex direction="column" gap={1}>
      <Text fontSize="2xs" fontWeight="semibold" color="text.placeholder" px={2} pt={2}>
        {title}
      </Text>
      {items.map((item) => (
        <Flex
          key={item.id}
          px={2}
          py={1.5}
          gap={2}
          alignItems="center"
          borderRadius="md"
          cursor="pointer"
          _hover={{ bg: 'surface.hover' }}
          onClick={() => onSelect(item)}
        >
          <Icon color="text.secondary" flexShrink={0}>
            <item.icon size={16} />
          </Icon>
          <Flex direction="column" gap={0} minW={0}>
            <Text fontSize="sm" fontWeight="medium" truncate>
              {item.label}
            </Text>
            <Text fontSize="2xs" color="text.secondary" truncate>
              {item.description}
            </Text>
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
};

const NodePickerPopover = ({ mode, onSelect, onClose }: Props) => {
  const [search, setSearch] = useState('');

  const filteredItems = useMemo(() => {
    if (search.trim() === '') {return null;}
    const query = search.toLowerCase();
    return ALL_CATALOG_ITEMS.filter(
      (item) =>
        item.label.toLowerCase().includes(query) ||
        item.description.toLowerCase().includes(query),
    );
  }, [search]);

  const title = mode === PickerMode.Replace ? 'Replace' : 'Add Node';

  const handleSelect = useCallback(
    (item: CatalogItem) => {
      onSelect(item);
      onClose();
    },
    [onSelect, onClose],
  );

  return (
    <Flex
      direction="column"
      bg="surface.background"
      borderWidth="1px"
      borderColor="border.light"
      borderRadius="xl"
      boxShadow="lg"
      w="280px"
      maxH="400px"
      overflow="hidden"
    >
      {/* Header */}
      <Flex px={3} pt={3} pb={2} alignItems="center" gap={2}>
        <Text fontSize="sm" fontWeight="semibold">
          {title}
        </Text>
      </Flex>
      {/* Search */}
      <Flex px={3} pb={2}>
        <Flex
          alignItems="center"
          gap={2}
          bg="surface.muted"
          borderRadius="md"
          px={2}
          w="full"
        >
          <Icon color="text.placeholder" flexShrink={0}>
            <LuSearch size={14} />
          </Icon>
          <Input
            placeholder="Search..."
            size="sm"
            variant="flushed"
            fontSize="sm"
            bg="transparent"
            border="none"
            boxShadow="none"
            _focus={{ boxShadow: 'none', borderColor: 'transparent' }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            _placeholder={{ color: 'text.placeholder' }}
          />
        </Flex>
      </Flex>
      {/* Content */}
      <Box overflowY="auto" px={1} pb={2} flex={1}>
        {filteredItems != null ? (
          filteredItems.length > 0 ? (
            <CategorySection title="Results" items={filteredItems} onSelect={handleSelect} />
          ) : (
            <Flex px={2} py={4} justifyContent="center">
              <Text fontSize="sm" color="text.placeholder">
                No results found
              </Text>
            </Flex>
          )
        ) : (
          <>
            <CategorySection title="Payments" items={FINANCE_CATALOG} onSelect={handleSelect} />
            <CategorySection title="Triggers" items={TRIGGER_CATALOG} onSelect={handleSelect} />
            <CategorySection title="Actions" items={ACTION_CATALOG} onSelect={handleSelect} />
            <CategorySection title="Routing" items={ROUTING_CATALOG} onSelect={handleSelect} />
          </>
        )}
      </Box>
    </Flex>
  );
};

export default memo(NodePickerPopover);
